import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableRow, Paper,
    TableHead, Button, TextField, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, IconButton, Box, Typography, Card, CardContent
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import Swal from 'sweetalert2';

function GestionTipoPartida() {
    const server = process.env.REACT_APP_API_ROUTE;
    const { user } = useUser();
    const { tienePermiso } = useAccesos();

    const [tiposPartida, setTiposPartida] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTipo, setEditingTipo] = useState(null);
    const [formData, setFormData] = useState({
        codigo: '',
        descripcionTipoPartida: '',
        automatica: false
    });
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [tipoToDelete, setTipoToDelete] = useState(null);

    useEffect(() => {
        cargarTipos();
    }, []);

    const cargarTipos = async () => {
        try {
            const response = await fetch(`${server}/CtbTipoPartida`);
            const data = await response.json();
            setTiposPartida(data.$values || data || []);
        } catch (error) {
            console.error('Error al cargar tipos de partida:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudieron cargar los tipos de partida'
            });
        }
    };

    const handleOpenDialog = (tipo = null) => {
        if (tipo) {
            setEditingTipo(tipo);
            setFormData({
                codigo: tipo.codigo || '',
                descripcionTipoPartida: tipo.descripcionTipoPartida || '',
                automatica: tipo.automatica || false
            });
        } else {
            setEditingTipo(null);
            setFormData({
                codigo: '',
                descripcionTipoPartida: '',
                automatica: false
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingTipo(null);
        setFormData({
            codigo: '',
            descripcionTipoPartida: '',
            automatica: false
        });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleGuardar = async () => {
        // Validaciones
        if (!formData.codigo.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo requerido',
                text: 'El código es obligatorio'
            });
            return;
        }

        if (!formData.descripcionTipoPartida.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Campo requerido',
                text: 'La descripción es obligatoria'
            });
            return;
        }

        try {
            const payload = {
                codigo: formData.codigo.trim(),
                descripcionTipoPartida: formData.descripcionTipoPartida.trim(),
                automatica: formData.automatica,
                ingresadoPor: editingTipo ? editingTipo.ingresadoPor : user?.codigoUsuario || 'ADMIN',
                modificadoPor: user?.codigoUsuario || 'ADMIN'
            };

            const method = editingTipo ? 'PUT' : 'POST';
            const endpoint = editingTipo
                ? `${server}/CtbTipoPartida/${editingTipo.id}`
                : `${server}/CtbTipoPartida`;

            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            Swal.fire({
                icon: 'success',
                title: 'Éxito',
                text: `Tipo de partida ${editingTipo ? 'actualizado' : 'creado'} correctamente`,
                timer: 2000,
                showConfirmButton: false
            });

            handleCloseDialog();
            cargarTipos();
        } catch (error) {
            console.error('Error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo guardar el tipo de partida'
            });
        }
    };

    const handleEliminar = async () => {
        if (!tipoToDelete) return;

        try {
            const response = await fetch(
                `${server}/CtbTipoPartida/${tipoToDelete.id}/${encodeURIComponent(user?.codigoUsuario || 'ADMIN')}`,
                { method: 'DELETE' }
            );

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            Swal.fire({
                icon: 'success',
                title: 'Éxito',
                text: 'Tipo de partida eliminado correctamente',
                timer: 1500,
                showConfirmButton: false
            });

            setDeleteDialog(false);
            setTipoToDelete(null);
            cargarTipos();
        } catch (error) {
            console.error('Error:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo eliminar el tipo de partida'
            });
        }
    };

    return (
        <div className="right_col" role="main">
            <div className="">
                <div className="page-title">
                    <div className="title_left">
                        <h3>Gestión de Tipos de Partida</h3>
                    </div>
                </div>
                <div className="clearfix"></div>

                <div className="row">
                    <div className="col-md-12 col-sm-12">
                        <div className="x_panel">
                            <div className="x_content">
                                <Box sx={{ mb: 2 }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleOpenDialog()}
                                    >
                                        Agregar Tipo de Partida
                                    </Button>
                                </Box>

                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                <TableCell>Código</TableCell>
                                                <TableCell>Descripción</TableCell>
                                                <TableCell>Automática</TableCell>
                                                <TableCell>Ingresado Por</TableCell>
                                                <TableCell>Fecha Ingreso</TableCell>
                                                <TableCell align="center">Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tiposPartida.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                        No hay tipos de partida registrados
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                tiposPartida.map((tipo) => (
                                                    <TableRow key={tipo.id} hover>
                                                        <TableCell>{tipo.codigo}</TableCell>
                                                        <TableCell>{tipo.descripcionTipoPartida}</TableCell>
                                                        <TableCell>
                                                            {tipo.automatica ? 'Sí' : 'No'}
                                                        </TableCell>
                                                        <TableCell>{tipo.ingresadoPor}</TableCell>
                                                        <TableCell>
                                                            {tipo.fechaIngreso ? new Date(tipo.fechaIngreso).toLocaleDateString() : 'N/A'}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleOpenDialog(tipo)}
                                                                title="Editar"
                                                                disabled={!tienePermiso('Editar Tipo Partida')}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => {
                                                                    setTipoToDelete(tipo);
                                                                    setDeleteDialog(true);
                                                                }}
                                                                title="Eliminar"
                                                                disabled={!tienePermiso('Eliminar Tipo Partida')}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Dialog de Crear/Editar */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingTipo ? 'Editar Tipo de Partida' : 'Agregar Tipo de Partida'}
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Código"
                        name="codigo"
                        value={formData.codigo}
                        onChange={handleInputChange}
                        required
                        margin="normal"
                        inputProps={{ maxLength: 4 }}
                    />
                    <TextField
                        fullWidth
                        label="Descripción"
                        name="descripcionTipoPartida"
                        value={formData.descripcionTipoPartida}
                        onChange={handleInputChange}
                        required
                        margin="normal"
                        multiline
                        rows={3}
                    />
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                            type="checkbox"
                            name="automatica"
                            checked={formData.automatica}
                            onChange={handleInputChange}
                            id="automatica-checkbox"
                        />
                        <label htmlFor="automatica-checkbox">
                            ¿Es automática?
                        </label>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleGuardar} variant="contained" color="primary" disabled={!tienePermiso('Editar Tipo Partida')}>
                        {editingTipo ? 'Actualizar' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Confirmación de Eliminación */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de eliminar el tipo de partida "{tipoToDelete?.descripcionTipoPartida}"?
                        Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(false)} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleEliminar} color="error" autoFocus disabled={!tienePermiso('Eliminar Tipo Partida')}>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default GestionTipoPartida;
